import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router'

import { SIDEBAR_ROW } from '@/components/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { NavGroup, SUB_ROW } from '../../DesignSystemNav'
import type { QuoteCardEntry, QuoteCardEntryGroup } from './quoteCardEntries'
import { CARD_PARAM } from './QuoteCardEntryPicker'

interface QuoteCardSideNavProps {
  groups: QuoteCardEntryGroup[]
  selectedId: string
}

function EntryLink({
  entry,
  selected,
  className,
}: {
  entry: QuoteCardEntry
  selected: boolean
  className: string
}) {
  return (
    <Link
      to={{ search: `?${CARD_PARAM}=${encodeURIComponent(entry.id)}` }}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        className,
        'aria-[current=page]:text-ink h-auto items-start py-1 aria-[current=page]:font-semibold'
      )}
    >
      <span className="flex min-w-0 flex-col items-start gap-0.5">
        <span className="max-w-full truncate">{entry.state}</span>
        {entry.carrier && (
          <span className="max-w-full truncate text-xs font-normal text-neutral-400">{entry.carrier}</span>
        )}
      </span>
    </Link>
  )
}

/** A group collapses behind its label, on the design system nav's sub-menu rail. */
function CollapsibleGroup({ group, selectedId }: { group: QuoteCardEntryGroup; selectedId: string }) {
  return (
    <Collapsible className="group/nav-menu" asChild>
      <li>
        <CollapsibleTrigger className={cn(SIDEBAR_ROW, 'cursor-pointer')}>
          <span>{group.label}</span>
          <ChevronRight className="ml-auto size-3! text-neutral-400 transition-transform duration-200 group-data-[state=open]/nav-menu:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <ul className="border-ink-200 mx-3.5 flex min-w-0 flex-col gap-1 border-l px-2.5 py-0.5">
            {group.entries.map((entry) => (
              <li key={entry.id}>
                <EntryLink entry={entry} selected={entry.id === selectedId} className={SUB_ROW} />
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  )
}

/** Every state on the page, one row each, drawn like the design system's own nav. */
export function QuoteCardSideNav({ groups, selectedId }: QuoteCardSideNavProps) {
  const render = (group: QuoteCardEntryGroup) =>
    group.label ? (
      <CollapsibleGroup key={group.label} group={group} selectedId={selectedId} />
    ) : (
      group.entries.map((entry) => (
        <li key={entry.id}>
          <EntryLink entry={entry} selected={entry.id === selectedId} className={SIDEBAR_ROW} />
        </li>
      ))
    )

  return (
    <nav aria-label="Quote card states" className="flex flex-col">
      <NavGroup>{groups.map(render)}</NavGroup>
    </nav>
  )
}
